import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'libs/styled';
import Image from '@fogia/core-web/components/Image';
import { above } from '@fogia/core-web/utils/mediaqueries';
import useBreakpoint from '@fogia/core-web/hooks/useBreakpoint';
import { show as showOverlay } from '@fogia/core-web/state/application/overlay';
import useAppDispatch from '@fogia/core-web/hooks/useAppDispatch';
import Text from 'components/Text';
import { ZoomIcon } from 'assets/media/icons/ZoomIcon';
import {
    ContentSliderDesktop,
    ContentSliderMobile,
    SliderHeader,
    type ContentSliderDesktopApi,
} from 'components/sliders/ContentSlider';

export interface ImageSliderImage {
    id: string | number;
    url: string;
    title: string;
}

export interface ImageSliderProps {
    images: ImageSliderImage[];
    productName?: string;
    text?: string;
}

const Section = styled('section')`
    display: block;
    padding: 40px 0;

    ${above['tablet.lg']} {
        padding: 80px 0;
    }
`;

const DesktopSlider = styled('div')``;

const MobileSlider = styled('div')``;

/** Breaks out of parent's 12px horizontal padding so slider is edge-to-edge on mobile */
const MobileBleedWrapper = styled('div')`
    margin-left: -12px;
    margin-right: -12px;
    width: calc(100% + 24px);
`;

const MobileInfoContent = styled('div')`
    display: flex;
    flex-direction: column;
    margin-top: 16px;
    width: 63%;
    margin-left: auto;
    margin-right: auto;
`;

const InfoGrid = styled('div')`
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 10px;
    margin-top: 16px;
`;

const InfoContent = styled('div')`
    grid-column: 4 / span 2;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ImageSlotInner = styled('div')`
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 5;
    overflow: hidden;
    background-color: var(--opacity-primary-30);
    cursor: pointer;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    > div {
        width: 100%;
        height: 100%;
    }
`;

const ZoomIconWrapper = styled('div', {
    shouldForwardProp: (prop: string) => prop !== '$visible',
})<{ $visible?: boolean }>`
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 1;
    pointer-events: none;
    color: var(--ui-primary);
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity var(--transition-slider, 0.5s ease);
`;

const ImageSlider = ({ images = [], productName = '', text = '' }: ImageSliderProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isDesktop = useBreakpoint({ from: 'tablet.lg' });
    const [desktopApi, setDesktopApi] = useState<ContentSliderDesktopApi | null>(null);

    const handleDesktopReady = useCallback((api: ContentSliderDesktopApi) => {
        setDesktopApi(api);
    }, []);

    const openZoomGallery = useCallback(
        (startIndex = 0) => {
            dispatch(
                showOverlay({
                    view: 'zoomGalleryOverlay',
                    transition: 'fade',
                    data: {
                        images,
                        startIndex,
                        productName,
                    },
                }),
            );
        },
        [dispatch, images, productName],
    );

    if (images.length === 0) {
        return null;
    }

    const handleImageKeyDown = (event: React.KeyboardEvent, index: number) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openZoomGallery(index);
        }
    };

    const renderImageSlot = (image: ImageSliderImage, index: number, imageWidths: number[], showZoomIcon: boolean) => (
        <ImageSlotInner
            onClick={() => openZoomGallery(index)}
            onKeyDown={(event) => handleImageKeyDown(event, index)}
            role="button"
            tabIndex={0}
            aria-label={image.title || productName}
        >
            <ZoomIconWrapper $visible={showZoomIcon}>
                <ZoomIcon />
            </ZoomIconWrapper>
            <Image
                src={{ url: image.url, width: imageWidths }}
                alt={image.title}
                title={image.title}
                objectFit="cover"
                loading="lazy"
            />
        </ImageSlotInner>
    );

    const renderDesktopItem = (image: ImageSliderImage, index: number, isActive?: boolean) =>
        renderImageSlot(image, index, [600, 900, 1200, 1600], isActive ?? false);

    const renderMobileSlide = (image: ImageSliderImage, index: number) =>
        renderImageSlot(image, index, [400, 600], true);

    const handleViewAllClick = useCallback(() => openZoomGallery(0), [openZoomGallery]);
    const viewAllLabel = t('imageSlider.view_all', { count: images.length });
    const galleryTitle = t('imageSlider.image_gallery');

    return (
        <Section>
            {!isDesktop && (
                <MobileSlider>
                    <SliderHeader
                        title={galleryTitle}
                        titleTypography={['Primary/Mobile/18_120_510_-3', null, null, 'Primary/15_120_510_-1']}
                        onLinkClick={handleViewAllClick}
                        linkLabel={viewAllLabel}
                        linkTypography={['Primary/Mobile/18_120_510_-3', null, null, 'Primary/15_120_510_-1']}
                    />
                    <MobileBleedWrapper>
                        <ContentSliderMobile
                            items={images}
                            getItemKey={(img) => img.id}
                            renderSlide={renderMobileSlide}
                            bleed={false}
                        />
                    </MobileBleedWrapper>
                    <MobileInfoContent>
                        <Text typography={['Primary/Mobile/13_100_510_-1']} m="0" color="var(--ui-primary)">
                            {productName}
                        </Text>
                        {text && (
                            <Text typography={['Secondary/Mobile/13_120_400_Indent']} m="0" color="var(--ui-primary)">
                                {text}
                            </Text>
                        )}
                    </MobileInfoContent>
                </MobileSlider>
            )}

            {isDesktop && (
                <DesktopSlider>
                    <SliderHeader
                        title={galleryTitle}
                        titleTypography={['Primary/Mobile/18_120_510_-3', null, null, 'Primary/15_120_510_-1']}
                        onLinkClick={handleViewAllClick}
                        linkLabel={viewAllLabel}
                        linkTypography={['Primary/Mobile/18_120_510_-3', null, null, 'Primary/15_120_510_-1']}
                        navApi={desktopApi}
                        showChevrons={images.length > 4}
                    />
                    <ContentSliderDesktop
                        items={images}
                        getItemKey={(img) => img.id}
                        renderItem={renderDesktopItem}
                        trackHeight={(activeWidth) => activeWidth * (5 / 4)}
                        onReady={handleDesktopReady}
                    />
                    <InfoGrid>
                        <InfoContent>
                            <Text
                                typography={['Primary/Mobile/13_100_510_-1', null, null, 'Primary/15_120_510_-1']}
                                m="0"
                                color="var(--ui-primary-aa)"
                            >
                                {productName}
                            </Text>
                            {text && (
                                <Text
                                    typography={['Secondary/Mobile/13_120_400', null, null, 'Secondary/15_120_400']}
                                    m="0"
                                    color="var(--ui-primary-aa)"
                                >
                                    {text}
                                </Text>
                            )}
                        </InfoContent>
                    </InfoGrid>
                </DesktopSlider>
            )}
        </Section>
    );
};

export default ImageSlider;
